import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../../components/ui/Badge';
import { Drawer } from '../../../components/ui/Drawer';
import { Button } from '../../../components/ui/Button';
import { Input, Label } from '../../../components/ui/Input';
import { STATUS_MAP, EMPTY_ADV } from '../utils';

export function InvoiceFilterModal({ open, onClose, initialValues, onApply, onReset }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(initialValues);

  useEffect(() => {
    if (open) setDraft(initialValues);
  }, [open, initialValues]);

  function set(key, val) {
    setDraft((prev) => ({ ...prev, [key]: val }));
  }

  function toggleStatus(key) {
    setDraft((prev) => ({
      ...prev,
      statuses: prev.statuses.includes(key)
        ? prev.statuses.filter((s) => s !== key)
        : [...prev.statuses, key],
    }));
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('invoices.filter_modal_title')}
      width="w-[420px]"
      footer={
        <>
          <Button variant="secondary" onClick={() => { setDraft(EMPTY_ADV); onReset(); }}>
            {t('common.reset')}
          </Button>
          <Button onClick={() => onApply(draft)}>
            {t('common.apply')}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <div>
          <Label>{t('invoices.filter_supplier')}</Label>
          <Input
            placeholder={t('invoices.filter_supplier_placeholder')}
            value={draft.supplier}
            onChange={(e) => set('supplier', e.target.value)}
          />
        </div>

        <div>
          <Label>{t('invoices.filter_invoice_date')}</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-[11px] text-slate-400 mb-1 block">{t('invoices.filter_date_from')}</span>
              <Input type="date" value={draft.invoiceDateFrom} onChange={(e) => set('invoiceDateFrom', e.target.value)} />
            </div>
            <div>
              <span className="text-[11px] text-slate-400 mb-1 block">{t('invoices.filter_date_to')}</span>
              <Input type="date" value={draft.invoiceDateTo} onChange={(e) => set('invoiceDateTo', e.target.value)} />
            </div>
          </div>
        </div>

        <div>
          <Label>{t('invoices.filter_due_date')}</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-[11px] text-slate-400 mb-1 block">{t('invoices.filter_date_from')}</span>
              <Input type="date" value={draft.dueDateFrom} onChange={(e) => set('dueDateFrom', e.target.value)} />
            </div>
            <div>
              <span className="text-[11px] text-slate-400 mb-1 block">{t('invoices.filter_date_to')}</span>
              <Input type="date" value={draft.dueDateTo} onChange={(e) => set('dueDateTo', e.target.value)} />
            </div>
          </div>
        </div>

        <div>
          <Label>{t('invoices.filter_erp_sent_period')}</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-[11px] text-slate-400 mb-1 block">{t('invoices.filter_date_from')}</span>
              <Input type="date" value={draft.erpSentFrom} onChange={(e) => set('erpSentFrom', e.target.value)} />
            </div>
            <div>
              <span className="text-[11px] text-slate-400 mb-1 block">{t('invoices.filter_date_to')}</span>
              <Input type="date" value={draft.erpSentTo} onChange={(e) => set('erpSentTo', e.target.value)} />
            </div>
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-slate-300 text-primary accent-primary"
              checked={draft.lateOnly}
              onChange={(e) => set('lateOnly', e.target.checked)}
            />
            <span className="text-[13px] text-slate-700">{t('invoices.filter_late_only')}</span>
          </label>
        </div>

        <div>
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-slate-300 text-primary accent-primary"
              checked={draft.incompleteOnly}
              onChange={(e) => set('incompleteOnly', e.target.checked)}
            />
            <span className="text-[13px] text-slate-700">{t('invoices.filter_incomplete_only')}</span>
          </label>
        </div>

        <div>
          <Label>{t('invoices.filter_status')}</Label>
          <div className="space-y-2 mt-1.5">
            {Object.entries(STATUS_MAP).map(([key, { variant, labelKey }]) => (
              <label key={key} className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-slate-300 text-primary accent-primary"
                  checked={draft.statuses.includes(key)}
                  onChange={() => toggleStatus(key)}
                />
                <Badge variant={variant}>{t(labelKey)}</Badge>
              </label>
            ))}
          </div>
        </div>
      </div>
    </Drawer>
  );
}
